import { useGetApiGroup, useGetApiInstitution, useGetApiLab } from '@smartsash/api-client';
import { useMemo } from 'react';

/**
 * How many rows a lookup query asks for.
 *
 * A lookup feeds a name map or a `Select`, not a list — it is deliberately not list state and never
 * reaches the URL. One page large enough to hold the whole vocabulary keeps it a single request;
 * the endpoints offer no "all" mode.
 *
 * It is 100 because that is the endpoint's ceiling: `pageSize` above it is silently clamped rather
 * than rejected, so the earlier `200` asked for twice what could ever arrive and read as if it had
 * received it. Beyond 100 records the join is incomplete and a name falls back to a dash — the
 * lasting fix is an embedded `{ id, name }` on the list DTOs, which the API does not yet carry.
 */
export const LOOKUP_PAGE_SIZE = 100;

/**
 * Institution `id → name`, for the columns that display a foreign key (HS-869).
 *
 * The list DTOs carry `institutionId` and no name (the FK-join policy), so the name is resolved
 * client-side. This lives in a hook rather than in each page because a data-screen definition hands
 * it to the table's `meta`: a column's `value` reader — the text search, sort and the filter builder
 * all read — cannot reach a second query on its own.
 *
 * Query key and parameters match what the pages used before, so the map costs no extra request
 * where a `Select` on the same screen already loads it.
 */
export function useInstitutionNames(): Map<string, string> {
  const { data } = useGetApiInstitution({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  return useNameMap(data?.items);
}

/** Group `id → name`, for the Laboratories group column. */
export function useGroupNames(): Map<string, string> {
  const { data } = useGetApiGroup({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  return useNameMap(data?.items);
}

/** Laboratory `id → name`, for the Devices and Users laboratory columns. */
export function useLabNames(): Map<string, string> {
  const { data } = useGetApiLab({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  return useNameMap(data?.items);
}

/**
 * Group `id → laboratory count`, for the Groups Laboratories column.
 *
 * `LabViewDTO` carries `groupId` but `GetApiLabParams` has no group filter, so the count is
 * derived client-side the same way the name lookups above are: fetch the lab list once and reduce
 * it into a `groupId → count` map.
 */
export function useLabCountByGroupId(): Map<string, number> {
  const { data } = useGetApiLab({ page: 1, pageSize: LOOKUP_PAGE_SIZE });
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const lab of data?.items ?? []) {
      if (!lab.groupId) continue;
      counts.set(lab.groupId, (counts.get(lab.groupId) ?? 0) + 1);
    }
    return counts;
  }, [data]);
}

/** Rows with an optional id and name → a lookup map, skipping anything half-filled. */
function useNameMap(
  items: { id?: string | null; name?: string | null }[] | null | undefined,
): Map<string, string> {
  return useMemo(() => {
    const names = new Map<string, string>();
    for (const item of items ?? []) {
      if (item.id != null && item.name != null) names.set(item.id, item.name);
    }
    return names;
  }, [items]);
}
